import React, { useState, useEffect, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Form, Formik } from "formik";
import { useThemeUI } from "theme-ui";
import * as Yup from "yup";

import { BackButton, SuccessIconWrap } from "./styled";

import BasicButton from "components/forms/BasicButton";
import PasswordInput from "components/forms/PasswordInput";
import LoginCard from "components/ui/LoginCard";
import H2 from "components/typography/H2";
import { useToast } from "components/toast";

import { completeRegistration } from "api/Auth";
import Subtext from "components/typography/Subtext";
import P from "components/typography/P";
import TextInput from "components/forms/TextInput";
import { BackArrowIcon } from "components/svg";
import PartyIcon from "components/svg/icons/PartyIcon";
import WarningIcon from "components/svg/icons/WarningIcon";
import { trackActivity } from "api/ActivityTracker";

const Register = () => {
  const themeContext = useThemeUI();
  const { theme } = themeContext;
  const [searchParams] = useSearchParams();
  const toast = useToast();

  const [regEmail,setEmail] = useState(searchParams.get("email"));
  const [validForm, setValidForm] = useState<boolean>(true);
  const [responseErrors, setResponseErrors] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<any>(null);
  const [successMessage, setSuccessMessage] = useState<any>("");

  const _onSubmit = async (values: any, actions: any) => {
    const userId = searchParams.get("userId");
    const token = searchParams.get("token");

    void trackActivity({
      module: "auth",
      action: "register_attempt",
      actionType: "click",
      userId: userId,
      metadata: {
        email: values.email,
        token,
      },
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
    });

    const res = await completeRegistration({ token : token || "", password : values.password, passwordConfirm: values.passwordConfirm });
    if (res?.success) {      
      setSuccessMessage("Success! You are officially registered.");
    } else {      
      toast.add(res?.message ? res.message : "Error finishing registration, please try again later" , "var(--theme-ui-colors-red)");
      setResponseErrors(true);
    }
  };

  // min 5 characters, 1 upper case letter, 1 lower case letter, 1 numeric digit.  
  const passwordRules = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{5,}$/;
  const UserSchema = Yup.object().shape({
    email: Yup.string().email("Invalid email").required("This field is required."),
    password: Yup.string().required("This field is required.").matches(passwordRules, { message: "Please create a stronger password" }),
    passwordConfirm: Yup.string().oneOf([Yup.ref("password"), undefined], "Passwords don't match.").required("Confirm Password is required."),
  });

  const hasTrackedView = useRef(false);

  useEffect(() => { 
    document.title = process.env.REACT_APP_NAME + " | Registration";
    if (!hasTrackedView.current) {
      hasTrackedView.current = true;
      const token = searchParams.get("token");
      const email = searchParams.get("email");
      void trackActivity({
        module: "auth",
        action: "register_view",
        actionType: "view",
        metadata: {
          token,
          email,
        },
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      });
    }

    const token = searchParams.get("token");
    setEmail(searchParams.get("email"));
    if (!token) {
      setValidForm(false);
      setErrorMessage("Invalid Link.");
    }
  }, [searchParams]);

  return (
    <LoginCard>    
        {!validForm && (
          <>
            <H2 textAlign="center" fontWeight="500" marginBottom="2rem">
              Registration Link Error    
            </H2>
            <SuccessIconWrap><WarningIcon fill={theme?.colors?.dkgrey} width="30px" height="30px"/> </SuccessIconWrap>
            {errorMessage && (                            
              <P color={theme?.colors?.danger} textAlign={"center"} fontWeight={"500"} fontFamily={"Poppins"} fontSize="14px">
                {errorMessage}
              </P>            
            )}
          </>
        )}          
        {validForm && (
          <>
          {successMessage && (
            <>
              <H2 textAlign="center" fontWeight="500" marginBottom="2rem">
                Registration
              </H2>  
              <SuccessIconWrap><PartyIcon fill={theme?.colors?.dkgrey} width="30px" height="30px"/> </SuccessIconWrap>                                          
              <P textAlign={"center"} fontFamily={"Poppins"} fontSize={"14px"} color={theme?.colors?.success} fontWeight={"500"}>
                {successMessage}
              </P>              
              <BackButton>
                <Link to={"/login"}>
                  <BackArrowIcon fill={theme?.colors?.body} width={"20px"} height={"auto"} margin={"0 10px 0 0"} />                      
                  <span>Go to Login</span>
                </Link>
              </BackButton>              
            </>
          )}
          {!successMessage && (
            <>              
              <H2 textAlign="center" fontWeight="500" marginBottom="2rem">
                Registration
              </H2>                               
              <Subtext color={theme?.colors?.dkgrey} textAlign="center" fontFamily={"Poppins"} fontSize="12px" marginBottom="30px">Passwords must be a minimum of 5 characters, include 1 uppercase letter, 1 lowercase letter, and 1 numeric digit.</Subtext>                              
              <Formik initialValues={{ email: regEmail?.replace(" ","+"), password: "", passwordConfirm: "" }} onSubmit={_onSubmit} validationSchema={UserSchema} enableReinitialize={true}>
                {({ isSubmitting, errors, values, submitCount, setFieldValue }) => {
                  return (
                    <Form noValidate autoComplete="off">
                      <TextInput 
                        type="email" 
                        name="email" 
                        label="Email" 
                        value={values.email}
                        onChange={(val: any) => setFieldValue("email",val)}
                        readonly={true} 
                        autoComplete="off" 
                        $errors={errors.email && submitCount > 0 ? errors.email : null} 
                        $responseErrors={responseErrors}
                      />
                      <PasswordInput 
                        name="password" 
                        label="Password" 
                        value={values.password || ""}
                        onChange={(val: any) => setFieldValue("password",val)}
                        autoComplete="new-password" 
                        $errors={errors.password && submitCount > 0 ? errors.password : null} 
                        $responseErrors={responseErrors}
                      />
                      <PasswordInput
                        name="passwordConfirm"
                        label="Confirm Password"
                        value={values.passwordConfirm || ""}
                        onChange={(val: any) => setFieldValue("passwordConfirm",val)}
                        $errors={errors.passwordConfirm && submitCount > 0 ? errors.passwordConfirm : null}
                      />
                      <BasicButton                            
                        type="submit"
                        $submitting={isSubmitting}
                        $loadingColor="#fff"
                        keepText={false}
                        styles={{width : "100%", margin : "10px auto 0px auto"}}
                      >Register</BasicButton>
                    </Form>
                  );
                }}
              </Formik>
            </>          
          )}
          </>
        )}     
    </LoginCard>
  );
};

export default Register;
